import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtNaira, symbolFor } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const QUICK_AMOUNTS = [5000, 10000, 20000, 50000];
const REASONS = ["Lunch", "Emergency", "Shopping", "Personal loan", "Transport", "Rent"];

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function AddDebt() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const cur = user?.default_currency;
  const [direction, setDirection] = useState("owed_to_me");
  const [name, setName] = useState("");
  const [personId, setPersonId] = useState(null);
  const [amount, setAmount] = useState("");
  const [phone, setPhone] = useState("");
  const [description, setDescription] = useState("");
  const [dateLent, setDateLent] = useState(todayISO());
  const [dueDate, setDueDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [showLimit, setShowLimit] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);

  const iOwe = direction === "i_owe";

  const { data: people = [] } = useQuery({
    queryKey: ["people"],
    queryFn: async () => (await api.get("/people")).data,
  });

  const matches = name.trim()
    ? people.filter((p) => p.name.toLowerCase().includes(name.trim().toLowerCase()))
    : [];

  const pickPerson = (p) => {
    setName(p.name);
    setPersonId(p.id);
    setPhone(p.phone || "");
    setSuggestOpen(false);
  };

  const save = async () => {
    const val = parseFloat(amount);
    if (!name.trim()) return toast.error(iOwe ? "Please enter who you owe." : "Please enter who owes you.");
    if (!val || val <= 0) return toast.error("Please enter a valid amount.");
    setLoading(true);
    try {
      const { data } = await api.post("/debts", {
        person_id: personId,
        person_name: personId ? null : name.trim(),
        person_phone: phone || null,
        amount: val,
        direction,
        description: description || null,
        date_lent: dateLent ? new Date(dateLent).toISOString() : null,
        due_date: dueDate ? new Date(dueDate).toISOString() : null,
      });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["debts"] });
      qc.invalidateQueries({ queryKey: ["people"] });
      navigate(`/add/success/${data.id}`, { replace: true });
    } catch (e) {
      if (apiError(e) === "free_limit_reached") {
        setShowLimit(true);
      } else {
        toast.error(apiError(e));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="debii-shell min-h-screen flex flex-col px-5 py-4">
      <div className="flex items-center gap-3">
        <button data-testid="add-back" onClick={() => navigate(-1)} className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center">
          <ArrowLeft className="h-5 w-5 text-primary" />
        </button>
        <h1 className="font-display text-xl font-bold">Add a record</h1>
      </div>

      {/* Direction toggle */}
      <div className="mt-5 grid grid-cols-2 gap-2 rounded-2xl bg-secondary p-1">
        <button
          data-testid="direction-owed-to-me"
          onClick={() => setDirection("owed_to_me")}
          className={`h-11 rounded-xl text-sm font-semibold transition-all ${!iOwe ? "bg-white text-primary shadow-sm" : "text-muted-foreground"}`}
        >
          Someone owes me
        </button>
        <button
          data-testid="direction-i-owe"
          onClick={() => setDirection("i_owe")}
          className={`h-11 rounded-xl text-sm font-semibold transition-all ${iOwe ? "bg-white text-primary shadow-sm" : "text-muted-foreground"}`}
        >
          I owe someone
        </button>
      </div>

      <div className="mt-6 flex-1 space-y-5">
        {/* Who */}
        <div className="relative">
          <label className="text-sm font-medium text-foreground">{iOwe ? "Who do you owe?" : "Who owes you?"}</label>
          <Input
            data-testid="debt-person-name-input"
            value={name}
            onChange={(e) => { setName(e.target.value); setPersonId(null); setSuggestOpen(true); }}
            onFocus={() => setSuggestOpen(true)}
            placeholder="Enter a name"
            className="mt-1.5 h-14 rounded-xl text-lg"
            autoFocus
          />
          {suggestOpen && matches.length > 0 && !personId && (
            <div className="absolute z-20 mt-1 w-full rounded-xl border border-border bg-white shadow-lg overflow-hidden">
              {matches.slice(0, 4).map((p) => (
                <button
                  key={p.id}
                  data-testid={`person-suggest-${p.id}`}
                  onClick={() => pickPerson(p)}
                  className="w-full text-left px-4 py-3 text-sm hover:bg-secondary flex items-center justify-between"
                >
                  <span className="font-medium">{p.name}</span>
                  <span className="text-xs text-muted-foreground">Existing</span>
                </button>
              ))}
            </div>
          )}
          {personId && <p className="mt-1 text-xs text-accent font-medium">Linked to existing person ✓</p>}
        </div>

        {/* Amount */}
        <div>
          <label className="text-sm font-medium text-foreground">How much?</label>
          <div className="mt-1.5 relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 font-display text-3xl font-bold text-muted-foreground">{symbolFor(cur)}</span>
            <Input
              data-testid="debt-amount-input"
              type="number"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              className="h-20 pl-11 text-3xl font-display font-extrabold tabular rounded-2xl"
            />
          </div>
          <div className="mt-2 flex gap-2 overflow-x-auto no-scrollbar">
            {QUICK_AMOUNTS.map((a) => (
              <button
                key={a}
                data-testid={`quick-amount-${a}`}
                onClick={() => setAmount(String(a))}
                className="shrink-0 rounded-full border border-border bg-white px-3.5 py-1.5 text-sm font-semibold text-foreground active:scale-95"
              >
                {fmtNaira(a, cur)}
              </button>
            ))}
          </div>
        </div>

        {/* Optional */}
        <div>
          <label className="text-sm font-medium text-foreground">Phone number <span className="text-muted-foreground font-normal">(optional)</span></label>
          <Input
            data-testid="debt-phone-input"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="e.g. 0803 123 4567"
            className="mt-1.5 h-12 rounded-xl"
          />
        </div>

        <div>
          <label className="text-sm font-medium text-foreground">What was it for? <span className="text-muted-foreground font-normal">(optional)</span></label>
          <Textarea
            data-testid="debt-description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Lunch, emergency, shopping, personal loan..."
            className="mt-1.5 rounded-xl resize-none"
            rows={2}
          />
          <div className="mt-2 flex gap-2 overflow-x-auto no-scrollbar">
            {REASONS.map((r) => (
              <button
                key={r}
                onClick={() => setDescription(r)}
                className="shrink-0 rounded-full border border-border bg-white px-3 py-1 text-xs font-medium text-muted-foreground active:scale-95"
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium text-foreground">{iOwe ? "Date borrowed" : "Date lent"}</label>
            <Input data-testid="debt-date-lent-input" type="date" value={dateLent} onChange={(e) => setDateLent(e.target.value)} className="mt-1.5 h-12 rounded-xl" />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">Repay by <span className="text-muted-foreground font-normal">(opt.)</span></label>
            <Input data-testid="debt-due-date-input" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1.5 h-12 rounded-xl" />
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 py-4 bg-background">
        <Button data-testid="debt-submit-button" onClick={save} disabled={loading} className="w-full h-14 rounded-2xl text-base">
          {loading ? "Saving…" : <><Check className="h-5 w-5" /> {iOwe ? "Save what I owe" : "Save Debt"}</>}
        </Button>
      </div>

      <Dialog open={showLimit} onOpenChange={setShowLimit}>
        <DialogContent className="max-w-[24rem] rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Free plan limit reached</DialogTitle>
            <DialogDescription>
              You've reached the {5} active-debt limit on the free plan. Upgrade to Debii Plus for unlimited active debts. Paid-off debts never count toward the limit.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button data-testid="limit-upgrade-button" onClick={() => navigate("/upgrade")} className="w-full h-11 rounded-xl">Upgrade to Debii Plus</Button>
            <Button variant="ghost" onClick={() => setShowLimit(false)} className="w-full h-10 rounded-xl">Not now</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
